import { ChevronDown, ChevronUp, RotateCw, X } from 'lucide-react';
import Button from './Button';
import { cx } from './util';

// §2.6 Upload sheet. The global queue that drives it lives in shell/uploads.js (HD-5 Qual wired it).
// files: [{ id, name, size (bytes), progress 0..1, status: 'queued'|'uploading'|'done'|'failed', error? }]
// onRetry(file) / onDismiss(file): the Retry and Remove buttons on a failed file. onCancel(file): a Cancel button on a
// queued or uploading file, shown when canCancel(file) is true (default: always). Close is enabled once nothing is
// queued or uploading.
const fmtSize = b => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);

export default function UploadSheet({ files = [], collapsed = false, onToggle, onClose, onRetry, onDismiss, onCancel, canCancel = () => true, inline = false }) {
  const done = files.filter(f => f.status === 'done').length;
  const failed = files.filter(f => f.status === 'failed').length;
  const active = files.length - done - failed;
  return (
    <section className={cx('ui ui-upload', inline && 'ui-upload--inline')} aria-label="Uploads">
      <header className="ui-upload__head">
        <span className="ui-upload__title">{active ? `Uploading ${done} of ${files.length}` : `${done} uploaded${failed ? `, ${failed} failed` : ''}`}</span>
        {onToggle && <Button variant="quiet" iconOnly icon={collapsed ? ChevronUp : ChevronDown} label={collapsed ? 'Show uploads' : 'Hide uploads'} onClick={onToggle} />}
        {onClose && <Button variant="quiet" iconOnly icon={X} label="Close uploads" onClick={onClose} disabled={active > 0} />}
      </header>
      {!collapsed && (
        <ul className="ui-upload__list">
          {files.map(f => (
            <li key={f.id} className="ui-upload__file">
              <span className="ui-upload__name ui-tc" title={f.name}>{f.name}</span>
              <span className="ui-meta">{f.status === 'failed' ? 'Failed' : f.status === 'done' ? fmtSize(f.size) : `${Math.round(f.progress * 100)}% of ${fmtSize(f.size)}`}</span>
              <span className={cx('ui-meter', f.status === 'failed' && 'ui-meter--danger')} role="progressbar" aria-label={f.name}
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((f.status === 'done' ? 1 : f.progress) * 100)}>
                <span className="ui-meter__fill" style={{ width: `${(f.status === 'done' ? 1 : f.progress) * 100}%` }} />
              </span>
              {f.error && <span className="ui-field__error ui-upload__err">{f.error}</span>}
              {f.status === 'failed' && (onRetry || onDismiss) && (
                <span className="ui-upload__acts">
                  {onRetry && <Button variant="quiet" icon={RotateCw} onClick={() => onRetry(f)}>Retry</Button>}
                  {onDismiss && <Button variant="quiet" onClick={() => onDismiss(f)}>Remove</Button>}
                </span>
              )}
              {onCancel && (f.status === 'queued' || f.status === 'uploading') && canCancel(f) && (
                <span className="ui-upload__acts">
                  <Button variant="quiet" onClick={() => onCancel(f)} aria-label={`Cancel ${f.name}`}>Cancel</Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
